<script setup lang="ts">
import type { ContentItemDownloadRequest } from '@metapic-apps/shared'
import {
  ContentItemDetails,
  ContentItemOverviewContent,
  DrawerComponent,
  LoadingComponent,
  NoteComponent,
  useMediaDownload,
} from '@metapic-apps/shared'
import { useFlag } from '@unleash/proxy-client-vue'
import { posthog } from 'posthog-js'
import { computed, nextTick, onBeforeUnmount, watch } from 'vue'
import { useStore } from 'vuex'

import { useAffiliateLinkMetrics } from '@/api/affiliate-link-metrics.api'
import { useGetContentItemDetails } from '@/api/content-items.api'
import { FLAG_CONTENT_DETAIL_MODAL } from '@/lib/constants/flags'
import { config, notification } from '@/lib/helpers'
import { socialMediaClient } from '@/lib/services/social-media.service'

const store = useStore()
const contentItemId = defineModel<string | null>('modelValue', { default: null })
defineProps<{
  hasPrevItem?: boolean
  hasNextItem?: boolean
}>()
const emits = defineEmits<{
  (event: 'onClose'): void
  (event: 'navigate', direction: 'prev' | 'next'): void
}>()

const isModalEnabled = useFlag(FLAG_CONTENT_DETAIL_MODAL)
const { data, isLoading, error } = useGetContentItemDetails(contentItemId)
const affiliateLinkId = computed(() => data.value?.affiliateLink?.id)
const { data: affiliateLinkMetrics } = useAffiliateLinkMetrics(affiliateLinkId)
const { downloadFile, isLoading: isDownloading } = useMediaDownload({
  getAssetDownloadUrl: socialMediaClient.getAssetDownloadUrl,
})
const isOpen = computed(() => Boolean(contentItemId.value))
let removeDrawerScrollListener: (() => void) | null = null

const handleClose = () => {
  contentItemId.value = null
  emits('onClose')
}

const handleDownloadContent = async (request: ContentItemDownloadRequest) => {
  try {
    await downloadFile(request)
  } catch {
    notification.error('Failed to download content. Please try again.')
    return
  }
  trackContentOverviewEvent('content_item_overview_download_button_clicked')
  notification.success('Media downloaded successfully')
}
const hideOrders = computed(() => store.getters['shop/hideSales'])
const hideCosts = computed(() => store.getters['shop/hideCosts'])

const handleContentItemChange = () => {
  trackContentOverviewEvent('content_item_overview_content_changed')
}

const trackContentOverviewEvent = (eventName: string) => {
  if (!config.isPosthogEnabled) {
    return
  }

  posthog.capture(eventName, { content_item_id: contentItemId.value })
}

const stopDrawerScrollTracking = () => {
  removeDrawerScrollListener?.()
  removeDrawerScrollListener = null
}

const startDrawerScrollTracking = async () => {
  stopDrawerScrollTracking()

  await nextTick()

  const scrollContainer = document.querySelector('.content-item-overview__content-container')
  if (!scrollContainer) {
    return
  }

  const handleScroll = () => {
    trackContentOverviewEvent('content_item_overview_content_scrolled')
    stopDrawerScrollTracking()
  }

  scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

  removeDrawerScrollListener = () => {
    scrollContainer.removeEventListener('scroll', handleScroll)
  }
}

watch(
  isOpen,
  (open) => {
    if (!open) {
      stopDrawerScrollTracking()
      return
    }

    void startDrawerScrollTracking()
  },
  { immediate: true },
)

onBeforeUnmount(() => stopDrawerScrollTracking())
</script>
<template>
  <ContentItemDetails
    v-if="isModalEnabled && isOpen"
    :data="data"
    :content-item-id="contentItemId"
    view-mode="advertiser"
    :is-loading="isLoading"
    :has-error="Boolean(error)"
    :is-downloading="isDownloading"
    :hide-costs="hideCosts"
    :hide-orders="hideOrders"
    :affiliate-link-metrics="affiliateLinkMetrics"
    :has-prev-item="hasPrevItem"
    :has-next-item="hasNextItem"
    @close="handleClose"
    @content-item-change="handleContentItemChange"
    @download-content="handleDownloadContent"
    @navigate="emits('navigate', $event)"
  />
  <DrawerComponent
    v-if="isOpen && !isModalEnabled"
    :model-value="isOpen"
    class="content-item-overview-drawer"
    size="920px"
    paddingless
    destroy-on-close
    fullscreen-on-mobile
    @closed="handleClose"
  >
    <LoadingComponent v-if="isLoading" :size="48" />
    <ContentItemOverviewContent
      v-else-if="data && contentItemId"
      :data="data"
      :content-item-id="contentItemId"
      view-mode="advertiser"
      :is-downloading="isDownloading"
      :hide-costs="hideCosts"
      :hide-orders="hideOrders"
      :affiliate-link-metrics="affiliateLinkMetrics"
      @on-close="handleClose"
      @content-item-change="handleContentItemChange"
      @download-content="handleDownloadContent"
    />
    <NoteComponent v-else-if="error"> Failed to load content item details. </NoteComponent>
  </DrawerComponent>
</template>
